import { useParams } from 'react-router-dom'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { newThemeObject } from '../../newTheme'
import { useSelector } from '../../redux/hooks/useSelector'
import { NavBarButton } from './NavBarButton'
import { Badge, Skeleton } from '@mui/material'
import { CreditCardRounded, DashboardRounded, HelpOutline, PublishRounded, DriveFolderUpload } from '@mui/icons-material'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { isClientUser } from '../../services/keycloak.service'
import { Pages } from '../../router/router'
import { useTranslation } from 'react-i18next'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import ListIcon from '@mui/icons-material/List';
import PaymentsIcon from '@mui/icons-material/Payments';
import { Portfolio } from '../../redux/reducers/portfolio'
import { INavBarButton } from '../../interfaces/layout'
import Divider from '../common/Divider'
import { FILE_IMPORT_DIALOG_ID } from '../../constants/dialogConstants'
import { useInstrumentApi } from '../../hooks/useInstrumentApi'
import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import LogoPortilio from '../../assets/logo-text3.svg'
import LogoPortilio2 from '../../assets/logo-text2.png'
import LogoApex from '../../assets/APEX-CAPITAL_2.svg'
import LogoImperium from '../../assets/IMPERIUM-FINANCE.png'
import { TransparentIconButton } from '../common/TransparentIconButton'
import { OrdersByStateCount } from '../../redux/reducers/orders'
import { isApexUser } from '../../utils/apex'
import { APEX, IMPERIAL_FINANCE } from '../../constants/businessTerritory'

function useButtons(isApexUser: boolean) {
  const { t } = useTranslation()
  const { clientId } = useParams<{ clientId: string }>()
  const buttons: INavBarButton[] = []
  const portfButtons: INavBarButton[] = []
  const managerButtons: INavBarButton[] = []
  const { exportOrders } = useInstrumentApi()
  const dispatch = useDispatch()

  const client = useSelector(state => state.clients.client)
  const portfolios = client.portfolios

  const [ordersByStateCount, setOrdersByStateCount] = useState([] as OrdersByStateCount[])
  const orderSse = useSelector(state => state.orders.orderSse)

  useEffect(() => {
    if (orderSse && orderSse.userStates && orderSse.userStates.length > 0) {
      setOrdersByStateCount(orderSse.userStates[0].states)
    }
  }, [orderSse])

  if (!isClientUser()) {
    if ( !isApexUser ) {
      managerButtons.push({
        icon: <MenuBookRoundedIcon style={{color: '#00000080'}}/>,
        link: `${Pages.TraderDiary}`,
        text: t('TRADER_DIARY'),
        id: 'button.id1'
      })
    }
    managerButtons.push({
      icon: <CreditCardRoundedIcon style={{color: '#00000080'}}/>,
      link: `${Pages.OrderList}`,
      text: isApexUser ? t('INVESTMENT_UNITS') : t('ORDERS_LIST'),
      id: 'button.id2'
    })
    managerButtons.push({
      icon: <DescriptionRoundedIcon style={{color: '#00000080'}}/>,
      link: `${Pages.InstrumentList}`,
      text: t('INSTRUMENTS_LIST'),
      id: 'button.id3'
    })
    managerButtons.push({
      icon: <PersonRoundedIcon style={{color: '#00000080'}}/>,
      link: `${Pages.Home}`,
      text: t('CLIENTS'),
      id: 'button.id3'
    })
    managerButtons.push({
      icon: <PublishRounded style={{color: '#00000080'}}/>,
      function: () => {
        dispatch(openDialog(FILE_IMPORT_DIALOG_ID))
      },
      text: t('FILE_IMPORT'),
      id: 'button.id5'
    })
    managerButtons.push({
      icon: (
        <TransparentIconButton
          icon={
            <Badge
              badgeContent={ordersByStateCount.find(count => count.state === 'OS_NEW')?.count || 0}
              style={{color: '#00000080'}}
            >
              <DescriptionRoundedIcon style={{ fontSize: '30px' }} />
            </Badge>
          }
        />
      ),
      function: exportOrders,
      text: t('ORDERS_EXPORT'),
      id: 'button.id6'
    })
  }

  if (!clientId) {
    return [buttons, portfButtons, managerButtons]
  }
  if (!isClientUser()) {
    buttons.push({
      icon: <DashboardRounded style={{color: '#00000080'}}/>,
      link: portfolios?.size>1?`${Pages.ClientDetail}/${clientId}`:'',
      text: t('ASSET_OVERVIEW'),
      id: 'button.id7'
    })
    portfolios.map((portfolio: Portfolio, index: number) => {
      buttons.push({
        link: `${Pages.ClientDetail}/${clientId}/${index}`,
        text: portfolio.name || (index + 1).toString(),
        id: 'button.id8'
      })
    })
    buttons.push({
      icon: <PaymentsIcon style={{color: '#00000080'}}/>,
      text: t('TRANSACTIONS'),
      id: 'button.id9'
    })
    buttons.push({
      link: `${Pages.ClientDetailDeal}/${clientId}`,
      text: t('DEAL_TRANSACTION'),
      id: 'button.id10'
    })
    buttons.push({
      link: `${Pages.ClientDetailCashflow}/${clientId}`,
      text: t('CASHFLOW_TRANSACTION'),
      id: 'button.id11'
    })
    buttons.push({
      icon: <ListIcon style={{color: '#00000080'}}/>,
      text: t('TAX_REPORT'),
      id: 'button.id12'
    })
    buttons.push({
      link: `${Pages.ClientDetailTaxPossition}/${clientId}`,
      text: t('TAX_REPORT_TRANSACTION'),
      id: 'button.id13'
    })
    buttons.push({
      link: `${Pages.ClientDetailTaxSummary}/${clientId}`,
      text: t('TAX_REPORT_SUMMARY'),
      id: 'button.id14'
    })
    buttons.push({
      icon: <CreditCardRounded style={{color: '#00000080'}}/>,
      link: `${Pages.ClientOrderList}/${clientId}`,
      text: t('ORDERS'),
      id: 'button.id15'
    })
    buttons.push({
      icon: <HelpOutline style={{color: '#00000080'}}/>,
      link: `${Pages.InvestmentQuestionnaire}/${clientId}`,
      text: t('INVESTMENT_QUESTIONNAIRE'),
      id: 'button.id16'
    })
    buttons.push({
      icon: <DriveFolderUpload style={{color: '#00000080'}}/>,
      link: `${Pages.ImportDocuments}/${clientId}`,
      text: t('IMPORT_DOCUMENTS'),
      id: 'button.id17'
    })
  } else {
    buttons.push({
      icon: <DashboardRounded style={{color: '#00000080'}}/>,
      link: `${Pages.ClientDetail}/${clientId}`,
      text: t('ASSET_OVERVIEW'),
      id: 'button.id18'
    })
    buttons.push({
      icon: <HelpOutline style={{color: '#00000080'}}/>,
      link: `${Pages.InvestmentQuestionnaire}/${clientId}`,
      text: t('INVESTMENT_QUESTIONNAIRE'),
      id: 'button.id19'
    })
    buttons.push({
      icon: <DriveFolderUpload style={{color: '#00000080'}}/>,
      link: `${Pages.ImportDocuments}/${clientId}`,
      text: t('IMPORT_DOCUMENTS'),
      id: 'button.id20'
    })
  }
  if ( isClientUser() && isApexUser ) {
    buttons.push({
      icon: <CreditCardRoundedIcon style={{color: '#00000080'}}/>,
      link: `${Pages.ClientOrderList}/${clientId}`,
      text: t('ORDERS_LIST'),
      id: 'button.id21'
    })
  }


  return [buttons, portfButtons, managerButtons]
}

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 700,
    height: '98%',
    background: '#fff',
    justifyContent: 'left',
    padding: '10px',
    gap: '5px',
    borderRadius: newThemeObject.roundness.basic,
    margin: 10,
  },
  logoWrapper: {
    width: '100%', 
    marginBottom: '5px',
    display: 'flex', 
    justifyContent: 'center'
  },
  logoPortilio: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    marginTop: 'auto',
  },
  logoPortilio2: {
    width: '170px',
    marginTop: '2px',
  },
  logoApex: {
    height: '100px',
    marginTop: '2px',
  },
  logoImperium: {
    height: '65px',
    width: '200px',
    marginTop: '10px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'center',
  },
  client: {
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#000',
    margin: 0,
    marginLeft: theme.spacing(4.5),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    textWrap: 'wrap',
    maxWidth: '80%',
  },
  text: {
    fontWeight: '600',
    fontSize: 12,
    margin: 0,
  },
  iconContainer: {
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: '30px',
    color: '#000',
  },
  wannaBeButtonWrapper: {
    display: 'flex',
    width: 250,
    justifyContent: 'left',
    padding: 10,
    paddingBottom: 0,
    borderRadius: newThemeObject.roundness.small,
    alignItems: 'center',
  },
}))

export const NavBar: React.FC = () => {
  const classes = useStyles()
  const client = useSelector(state => state.clients.client)
  const user = useSelector(state => state.auth.user);
  const { t } = useTranslation()
  const openedClient = useParams<{ clientId: string }>().clientId
  const [buttons, portfButtons, managerButtons] = useButtons(isApexUser(user))

  return (
    <div className={classes.wrapper}>
      <div className={classes.logoWrapper}>
      {user && user.businessTerritoryId === IMPERIAL_FINANCE &&
        <img src={LogoImperium} className={classes.logoImperium}/>
      }
      {user && user.businessTerritoryId === APEX &&
        <img src={LogoApex} className={classes.logoApex}/>
      }
      {!user || ( user.businessTerritoryId!==APEX && user.businessTerritoryId!==IMPERIAL_FINANCE) && 
        <img src={LogoPortilio2} className={classes.logoPortilio2}/>
      }
      </div>
      {!!parseInt(openedClient) && !(client.firstName || client.surname) && (
        <Skeleton className={classes.client} variant="text" height={25} />
      )}
      {managerButtons && managerButtons.length !== 0 && (
        <>
          {managerButtons.map(button => (
            <NavBarButton button={button} />
          ))}
          {buttons && buttons.length !== 0 && <Divider size="sm" />}
        </>
      )}
      {buttons?.map(button => (
        <NavBarButton button={button} />
      ))}

            <img src={LogoPortilio} alt="Multi tag" className={classes.logoPortilio}/>

    </div>
  )
}
